import { createFileRoute, useNavigate, Outlet, useRouterState, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Bot, Plus, Settings as SettingsIcon, Tags, Store, ChevronRight } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PartnerBot } from '@/api/types';
import { Skeleton, SkeletonCard, SkeletonGrid, SkeletonLine } from '@/components/ui/skeleton';
import { UnresponsiveBots } from '@/components/UnresponsiveBots';

export const Route = createFileRoute('/bots')({ component: Bots });

const FALLBACK_FRANCHISE = import.meta.env.VITE_FRANCHISE_BOT_USERNAME || '';

// Универсальная навигация к деталям бота. В iOS Telegram WKWebView
// TanStack Router navigate() иногда не срабатывает (известный нюанс с
// history API в Mini App контексте) — добавлен fallback на window.location
// если URL не изменился за 200ms.
type NavFn = ReturnType<typeof useNavigate>;
function goToBot(nav: NavFn, botId: string) {
  const target = `/bots/${botId}`;
  const before = window.location.pathname;
  try {
    void nav({ to: target });
  } catch {
    // ignore — упадём в fallback
  }
  window.setTimeout(() => {
    if (window.location.pathname === before) {
      window.location.assign(target);
    }
  }, 200);
}

function Bots() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // TanStack Router file-based routing считает bots.\$botId.tsx ДОЧЕРНИМ
  // маршрутом для bots.tsx. На /bots/<id> рендерится этот компонент + Outlet
  // с детьми. Без Outlet ребёнок не отрисуется → URL меняется, контент нет.
  const onChildRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith('/bots/'),
  });
  // Polling 8s — нужен чтобы только-что созданный (через managed-bot deeplink)
  // бот появлялся в списке без перезахода в WebApp. На детальном роуте
  // enabled:false → polling автоматически останавливается.
  const { data: bots, isPending: botsPending } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    refetchInterval: 8_000,
    staleTime: 4_000,
    enabled: !onChildRoute,
    placeholderData: keepPreviousData,
  });

  // Имя франш-бота для футера (кэш из dashboard, env как fallback). Чисто декоративно.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me(), enabled: !onChildRoute });
  // Магазины клиентов (идея #4): раньше до них добирались только через «Маркетинг».
  const mirrorsQ = useQuery({
    queryKey: ['mirrors'],
    queryFn: api.mirrors,
    enabled: !onChildRoute,
    placeholderData: keepPreviousData,
  });
  const franchiseHandle = me?.franchiseBotUsername || FALLBACK_FRANCHISE;

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  // Боты, чей тумблер уже в полёте: `disabled` доходит до кнопки только после
  // рендера, а второй щелчок двойного клика приходит раньше.
  const togglingIds = useRef(new Set<string>());
  const { mutate: toggleActive, isPending: toggling, variables: toggleVars } = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setBotActive(id, isActive).then(() => ({ id, isActive })),
    onMutate: async ({ id, isActive }) => {
      await qc.cancelQueries({ queryKey: ['bots'] });
      const prev = qc.getQueryData<PartnerBot[]>(['bots']);
      qc.setQueryData<PartnerBot[]>(['bots'], (curr) =>
        (curr || []).map((b) => (b.id === id ? { ...b, status: isActive ? 'active' : 'paused' } : b)),
      );
      return { prev };
    },
    onSuccess: ({ isActive }) => {
      haptic('success');
      toast.success(isActive ? t('Toggle bot active') : t('Toggle bot paused'));
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['bots'], ctx.prev);
      haptic('error');
      toast.error('Не удалось изменить статус бота. Попробуйте ещё раз.');
    },
    onSettled: (_data, _err, vars) => {
      togglingIds.current.delete(vars.id);
      qc.invalidateQueries({ queryKey: ['bots'] });
    },
  });

  // Условный return ПОСЛЕ всех хуков — иначе React Rules of Hooks: количество
  // вызванных хуков меняется между рендерами → 'Rendered fewer hooks than expected'.
  if (onChildRoute) {
    return <Outlet />;
  }

  const goPricing = (botId: string) => {
    const target = `/pricing?botId=${botId}`;
    const before = window.location.pathname;
    try {
      void navigate({ to: '/pricing', search: { botId } });
    } catch {
      /* fallback */
    }
    window.setTimeout(() => {
      if (window.location.pathname === before) window.location.assign(target);
    }, 200);
  };

  return (
    <div className="p-4 space-y-4 pb-28">
      <div className="flex items-center justify-between px-1 pt-2 pb-1">
        <h2 className="font-display text-[26px] font-bold tracking-tight">{t('Bots')}</h2>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            navigate({ to: '/onboarding/bot' });
          }}
          aria-label="Добавить бота"
          className="w-[42px] h-[42px] rounded-[14px] bg-primary text-primary-foreground flex items-center justify-center active:scale-90 transition-transform"
        >
          <Plus className="w-5 h-5" strokeWidth={2.4} />
        </button>
      </div>

      {/* Боты, которых погасил монитор. Над списком: если живых ботов нет вовсе,
          партнёр всё равно должен увидеть, что клиентов можно вернуть. */}
      <UnresponsiveBots enabled={me?.isDelegate === false} />

      {botsPending ? (
        // Без этой ветки экран во время загрузки уверенно предлагал «создайте
        // бота» партнёру, у которого он давно есть, — читалось как «бот пропал».
        <BotsSkeleton />
      ) : !bots || bots.length === 0 ? (
        <div className="surface-card rounded-[24px] p-9 text-center flex flex-col items-center">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.07] text-primary-light flex items-center justify-center mb-4">
            <Bot className="w-7 h-7" strokeWidth={1.6} />
          </div>
          <h3 className="font-display font-bold text-base mb-1.5">{t('Bots empty')}</h3>
          <p className="text-sm text-muted-foreground mb-5">{t('Bots empty desc')}</p>
          <button
            type="button"
            onClick={() => {
              haptic('light');
              navigate({ to: '/onboarding/bot' });
            }}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-2xl text-sm font-bold active:scale-[0.97] transition-transform"
          >
            {t('Create bot')}
          </button>
        </div>
      ) : (
        <>
          {bots.map((bot) => {
            const isPaused = bot.status === 'paused';
            return (
              <div key={bot.id} className="surface-card-raised rounded-[24px] p-[18px]">
                <div className="flex items-center gap-3.5">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => goToBot(navigate, bot.id)}
                    className="w-[50px] h-[50px] rounded-[15px] bg-white/[0.07] text-primary-light flex items-center justify-center shrink-0 active:scale-95 transition-transform cursor-pointer"
                  >
                    <Bot className="w-[26px] h-[26px]" strokeWidth={1.7} />
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => goToBot(navigate, bot.id)}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <div className="font-display font-semibold text-lg truncate">{bot.brandName}</div>
                    <div className="font-mono text-xs text-dim truncate mt-px">@{bot.username}</div>
                    <span
                      className={`inline-flex items-center gap-1.5 mt-[7px] font-display font-semibold text-[10.5px] px-[9px] py-[3px] rounded-full ${
                        isPaused
                          ? 'bg-[rgba(240,176,78,0.14)] text-amber'
                          : 'bg-[rgba(56,224,165,0.13)] text-money-light'
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {isPaused ? t('Bot status paused') : t('Bot status active')}
                    </span>
                  </div>
                  {/* Пока запрос летит, тумблер этого бота заблокирован: двойной тап слал
                      две паузы, а тап через долю секунды «паузу» и «включить» разом, и
                      итог зависел от порядка ответов. */}
                  <Switch
                    checked={!isPaused}
                    disabled={toggling && toggleVars?.id === bot.id}
                    aria-label={isPaused ? `Включить @${bot.username}` : `Поставить @${bot.username} на паузу`}
                    onCheckedChange={() => {
                      if (togglingIds.current.has(bot.id)) return;
                      togglingIds.current.add(bot.id);
                      toggleActive({ id: bot.id, isActive: isPaused });
                    }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className="surface-inset rounded-[16px] p-3">
                    <div className="eyebrow leading-[1.4]">Юзеры</div>
                    <div className="font-display font-bold text-[20px] nums mt-1.5 flex items-baseline gap-1.5">
                      {bot.totalUsers}
                      {bot.newUsersToday > 0 && (
                        <span className="text-[12px] font-bold text-money-light">+{bot.newUsersToday}</span>
                      )}
                    </div>
                    <div className="text-[9px] text-dim mt-0.5">сегодня +{bot.newUsersToday}</div>
                  </div>
                  <div className="surface-inset rounded-[16px] p-3">
                    <div className="eyebrow leading-[1.4]">{t('Active subs')}</div>
                    <div className="font-display font-bold text-[20px] nums mt-1.5">{bot.activeSubs}</div>
                  </div>
                  <div className="surface-inset rounded-[16px] p-3">
                    <div className="eyebrow">{t('MRR')}</div>
                    <div className="font-display font-bold text-[20px] nums mt-1.5">
                      {bot.mrrRub} <span className="text-muted-foreground text-[13px]">₽</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2.5 mt-3">
                  <button
                    type="button"
                    onClick={() => goToBot(navigate, bot.id)}
                    className="flex items-center justify-center gap-1.5 py-3 rounded-[14px] bg-white/[0.07] text-primary-light font-bold text-[13.5px] active:scale-[0.97] transition-transform"
                  >
                    <SettingsIcon className="w-4 h-4" strokeWidth={1.8} />
                    {t('Manage')}
                  </button>
                  <button
                    type="button"
                    onClick={() => goPricing(bot.id)}
                    className="flex items-center justify-center gap-1.5 py-3 rounded-[14px] bg-[rgba(56,224,165,0.12)] text-money-light font-bold text-[13.5px] active:scale-[0.97] transition-transform"
                  >
                    <Tags className="w-4 h-4" strokeWidth={1.8} />
                    {t('Pricing')}
                  </button>
                </div>
              </div>
            );
          })}

          <Link
            to="/mirrors"
            className="surface-card rounded-[20px] p-4 flex items-center gap-3.5 active:scale-[0.99] transition-transform"
          >
            <div className="w-11 h-11 rounded-[13px] bg-[rgba(52,211,153,0.14)] text-[#34d399] flex items-center justify-center shrink-0">
              <Store className="w-[21px] h-[21px]" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[15px]">{t('Mirrors title')}</div>
              {mirrorsQ.isPending ? (
                <SkeletonLine size="sm" className="w-40 mt-1" />
              ) : (
                <div className="text-xs text-dim mt-0.5">
                  {(mirrorsQ.data?.rows.length ?? 0) > 0
                    ? t('Mirrors summary', {
                        count: mirrorsQ.data?.rows.length ?? 0,
                        earned: `$${((mirrorsQ.data?.total_earned_usd_minor ?? 0) / 100).toFixed(2)}`,
                      })
                    : t('Mirrors empty hint')}
                </div>
              )}
            </div>
            <ChevronRight className="w-[18px] h-[18px] text-dim shrink-0" />
          </Link>

          {franchiseHandle && (
            <div className="text-center pt-3 font-mono text-[11px] text-[#5a5c63]">@{franchiseHandle}</div>
          )}
        </>
      )}
    </div>
  );
}

/** Карточка бота: иконка, название с @username и статусом, свитч, три плитки цифр. */
function BotsSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[24px] p-[18px] space-y-4">
          <div className="flex items-center gap-3.5">
            <Skeleton className="w-[50px] h-[50px] rounded-[15px] shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
              <SkeletonLine size="title" className="w-2/5" />
              <SkeletonLine size="sm" className="w-1/3" />
              <Skeleton className="h-[19px] w-24 rounded-full" />
            </div>
            <Skeleton className="w-11 h-6 rounded-full shrink-0" />
          </div>
          <SkeletonGrid
            count={3}
            cols={3}
            variant="flat"
            className="gap-2"
            itemClassName="h-[74px] rounded-[16px]"
          />
        </SkeletonCard>
      ))}
    </>
  );
}
